/* Customer statement (Duncan 2026-10-15): every open invoice a customer has with E3, to print or
   save as a PDF and send them. Route /ar/statement/:code (code = the customer's CE customer number,
   lib/statement); reached from AR / Collections → Statements.

   Carries what E3's CE statement carries (his copy: ANSON-D-02): the letterhead with phone and fax,
   Job # / Customer # / customer phone and fax / date, Invoice Number · Invoice Date · Due Date ·
   Amount · Applied · Past Due Balance · Current Balance, the aging Current · 0–30 · 31–60 · 61–90 ·
   91–120 · 121+ · Amount Due, the ACH note and the thank-you (Duncan: "thank you for the chance to earn
   your business"), in this page's own
   layout ("I LOVE the format of your statement"). CE prints one statement per job; this one can
   take several (untick a job to leave it off), and then each job's invoices sit under a Job # line.

   Laid out like the Project Status Report (PsrReportPage.js): US Letter sheets at 816 × 1056 px in
   a portal on <body>, the app hidden when printing. Every block has a fixed height and every row is
   one line, so the pages are counted rather than measured; the aging and remit-to block stays with
   the last rows.

   Bill to: CE's own customer record (Duncan 2026-10-17: "the same as CE's statement") — the jobs'
   CE customer number as Customer #, and its name, address lines, phone and fax from CE's Customer
   List (customer.record, lib/statement) once the agent has pulled it, under "Attn: Accounts
   Payable" like CE's. Until then the name comes from the CE job names and the address, phone and
   fax are typed on the page. Anything can be
   corrected on the page before printing; nothing typed there is saved. */
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { getInvoiceHistory } from '../api';
import { loadPsrFonts } from '../lib/psrData';
import { AGING, agedItems, agingOf, monthName, openByCustomer, postedThrough } from '../lib/statement';
import { todayCentral } from '../lib/invoiceHistory';

/* ── the report's design tokens (PsrReportPage.js) ── */
const INK = '#1F1D1A';
const INK2 = '#4A463F';
const GREY = '#6B665E';
const LINE = '#D6D2CA';
const FILL = '#F5F3EF';
const GREEN_HEAD = '#41593A';
const GREEN_SPENT = '#5A7A4D';
const RED = '#B3402E';
const SANS = "'Geist', system-ui, sans-serif";
const MONO = "'Geist Mono', 'SF Mono', Menlo, Consolas, monospace";

/* ── E3 (Duncan 2026-10-15: remit to the office, "#100"; questions go to the office phone and the
   accounting address, not a person: "no name should be"; the fax is on the CE statement) ── */
/* The bill-to's Attn line, as on E3's CE statement for every customer (Duncan 2026-10-17: yes, always
   "Attn: Accounts Payable" — not the customer's contact, who is a person like the superintendent). */
const ATTN = 'Attn: Accounts Payable';

const E3 = {
  name: 'E3 Entegral Solutions, Inc.', street: '2040 Highland Village Road #100', city: 'Highland Village, TX 75077',
  phone: '972-325-1919', fax: '972-325-1920', email: 'accounting@e3es.com', logo: '/e3-logo.png',
};

/* ── page geometry: every block has a fixed height, so the pages can be counted ── */
const PAGE_W = 816;
const PAGE_H = 1056;
const PAD_TOP = 54;
const PAD_X = 60;
const CONTENT_H = 916;  // content top (54) to 16px above the footer rule (y 986)
const P1_HEAD = 214;    // letterhead 84 + 16 + bill-to 112 + 2px rule
const RUN_HEAD = 42;    // pages 2+: one line + 2px rule
const THEAD = 54;       // 18 above + a two-line 36px head row
const ROW_H = 30;       // an invoice, or a job's heading
const CLOSE_H = 262;    // 18 + aging 56 + 20 + remit-to / questions 92 + 14 + ACH note 32 + 8 + thanks 18 (+4)
const cap = (head, withClose) => Math.floor((CONTENT_H - head - THEAD - (withClose ? CLOSE_H : 0)) / ROW_H);

/* Lines per page: as many as fit; a job's heading never ends a page; with several jobs a later
   page keeps a line for the "(continued)" heading of the job it starts in; and the closing block
   stays with the last lines (they move to a new page rather than leave it alone on one). Each
   page is a [from, to) range of the lines. */
export function paginate(lines, multiJob) {
  const room = (first, withClose) => cap(first ? P1_HEAD : RUN_HEAD, withClose) - (!first && multiJob ? 1 : 0);
  const pages = [];
  let at = 0;
  do {
    let take = Math.min(room(!pages.length, false), lines.length - at);
    if (at + take < lines.length && take > 1 && lines[at + take - 1].kind === 'group') take -= 1;
    pages.push([at, at + take]);
    at += take;
  } while (at < lines.length);
  const last = pages.length - 1;
  const fits = room(!last, true);
  const [from, to] = pages[last];
  if (to - from > fits) {
    let cut = from + fits;
    if (cut > from + 1 && lines[cut - 1].kind === 'group') cut -= 1;
    pages[last] = [from, cut];
    pages.push([cut, to]);
  }
  return pages;
}

const MINUS = '−';
const money = (v) => {
  const r = Math.round((Number(v) || 0) * 100) / 100;
  const abs = Math.abs(r).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return r < 0 ? `${MINUS}$${abs}` : `$${abs}`;
};
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtDay = (ymd) => {
  const p = ymd ? String(ymd).slice(0, 10).split('-').map(Number) : null;
  return p ? `${MONTHS[p[1] - 1]} ${p[2]}, ${p[0]}` : '–';
};

const label = { fontSize: 10.5, fontWeight: 600, letterSpacing: '.1em', textTransform: 'uppercase', color: GREY };
/* A figure's font size so it fits its box (Geist Mono: 0.6em a character), never above `max`:
   Ferris ISD's $1,338,738.69 ran its ".69" past the Amount due box at 20px (Duncan 2026-10-19). */
const fitMono = (text, width, max) => Math.min(max, Math.floor((width / (String(text).length * 0.6)) * 10) / 10);
// The CE statement's columns across the 696px page. Measured in Geist at these sizes, with 5px
// either side, the widest values are "May 28, 2026" 77px and "−$1,428,245.90" (a live credit) 101px.
const COLS = [
  { key: 'number', label: ['Invoice', 'Number'], w: 78 },
  { key: 'date', label: ['Invoice', 'Date'], w: 88 },
  { key: 'due', label: ['', 'Due Date'], w: 88 },
  { key: 'amount', label: ['', 'Amount'], w: 112, right: true },
  { key: 'applied', label: ['', 'Applied'], w: 106, right: true },
  { key: 'pastDue', label: ['Past Due', 'Balance'], w: 112, right: true },
  { key: 'current', label: ['Current', 'Balance'], w: 112, right: true },
];
const W = Object.fromEntries(COLS.map((c) => [c.key, c.w]));
const cell = { padding: '0 5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

/* An invoice: what's applied (payments and credits) is its amount less its balance; the balance
   sits under Past Due or Current, as on the CE statement (a credit follows its invoice). */
const Row = ({ i }) => {
  const late = i.age > 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', height: ROW_H, boxSizing: 'border-box', borderBottom: `1px solid ${LINE}`, fontSize: 12 }}>
      <div style={{ ...cell, width: W.number, fontFamily: MONO }}>{i.number}</div>
      <div style={{ ...cell, width: W.date }}>{fmtDay(i.date || i.paid_on)}</div>
      <div style={{ ...cell, width: W.due, color: INK2 }}>{i.due ? fmtDay(i.due) : '–'}</div>
      <div style={{ ...cell, width: W.amount, textAlign: 'right', fontFamily: MONO }}>{money(i.amount)}</div>
      <div style={{ ...cell, width: W.applied, textAlign: 'right', fontFamily: MONO, color: INK2 }}>{money(i.amount - i.open)}</div>
      <div style={{ ...cell, width: W.pastDue, textAlign: 'right', fontFamily: MONO, fontWeight: 600, color: RED }}>{late ? money(i.open) : ''}</div>
      <div style={{ ...cell, width: W.current, textAlign: 'right', fontFamily: MONO, fontWeight: 600 }}>{late ? '' : money(i.open)}</div>
    </div>
  );
};
const JobLine = ({ job, continued }) => (
  <div style={{ display: 'flex', alignItems: 'center', height: ROW_H, boxSizing: 'border-box', borderBottom: `1px solid ${LINE}`, background: FILL, fontSize: 12, padding: '0 5px' }}>
    <span style={{ ...label, fontSize: 10, color: INK2 }}>Job #</span>
    <span style={{ fontFamily: MONO, fontWeight: 600, marginLeft: 8 }}>{job}</span>
    {continued && <span style={{ color: GREY, marginLeft: 6 }}>(continued)</span>}
  </div>
);

const TableHead = () => (
  <div style={{ display: 'flex', alignItems: 'flex-end', height: 36, boxSizing: 'border-box', marginTop: 18, paddingBottom: 4, borderBottom: `1.5px solid ${INK2}`, ...label, color: INK2, lineHeight: '13px' }}>
    {COLS.map((c) => (
      <div key={c.key} style={{ ...cell, width: c.w, textAlign: c.right ? 'right' : 'left' }}>
        <div>{c.label[0] || ' '}</div>
        <div>{c.label[1]}</div>
      </div>
    ))}
  </div>
);

const Sheet = ({ page, pages, footer, children }) => (
  <div className="stm-sheet" style={{ width: PAGE_W, height: PAGE_H, boxSizing: 'border-box', background: '#fff', color: INK, fontFamily: SANS, fontSize: 13, lineHeight: 1.4, position: 'relative', padding: `${PAD_TOP}px ${PAD_X}px 0`, overflow: 'hidden' }}>
    {children}
    <div style={{ position: 'absolute', left: PAD_X, right: PAD_X, bottom: 40, display: 'flex', justifyContent: 'space-between', gap: 16, paddingTop: 8, borderTop: `1px solid ${LINE}`, fontSize: 11.5, lineHeight: '16px', color: GREY }}>
      <div style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{footer}</div>
      <div style={{ whiteSpace: 'nowrap' }}>Page {page} of {pages}</div>
    </div>
  </div>
);

/* A label / value line of the block beside Bill to. */
const Field = ({ name, children }) => (
  <div style={{ display: 'flex', gap: 10, height: 19, alignItems: 'baseline', fontSize: 12 }}>
    <span style={{ width: 104, flex: 'none', textAlign: 'right', color: GREY }}>{name}</span>
    <span style={{ minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: INK }}>{children}</span>
  </div>
);

const StatementPage = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [fontsReady, setFontsReady] = useState(false);
  const [left, setLeft] = useState(() => new Set()); // jobs left off
  const [name, setName] = useState(null);           // the bill-to name, once corrected

  useEffect(() => {
    let alive = true;
    getInvoiceHistory()
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setError(e.message || 'Could not load the open invoices.'); });
    loadPsrFonts().then(() => { if (alive) setFontsReady(true); });
    return () => { alive = false; };
  }, []);
  useEffect(() => {
    document.body.classList.add('stm-open');
    return () => document.body.classList.remove('stm-open');
  }, []);

  const customer = useMemo(() => (data ? openByCustomer(data).find((c) => c.code === code) || null : null), [data, code]);
  const items = useMemo(() => (customer ? agedItems(customer.items.filter((i) => !left.has(i.job))) : []), [customer, left]);
  const jobs = useMemo(() => [...new Set(items.map((i) => i.job))], [items]);
  const multiJob = jobs.length > 1;
  // One job: its invoices. Several: each job's invoices under its Job # line.
  const lines = useMemo(() => (multiJob
    ? jobs.flatMap((job) => [{ kind: 'group', job }, ...items.filter((i) => i.job === job).map((i) => ({ kind: 'inv', i }))])
    : items.map((i) => ({ kind: 'inv', i }))), [items, jobs, multiJob]);
  const aging = useMemo(() => agingOf(items), [items]);
  const pages = useMemo(() => (lines.length ? paginate(lines, multiJob) : []), [lines, multiJob]);
  const billTo = name != null ? name : (customer ? customer.name : '');
  const record = customer ? customer.record : null; // CE's Customer List (lib/statement)
  const asOf = data ? fmtDay(data.as_of) : '';
  // What billing this statement includes (lib/statement): on screen only, for whoever prints it.
  const posted = data ? postedThrough(data, todayCentral()) : null;
  const jobText = jobs.length === 1 ? jobs[0] : `${jobs.length} jobs, listed below`;

  useEffect(() => {
    const prev = document.title;
    if (customer) document.title = `Statement ${customer.code} — ${billTo}`;
    return () => { document.title = prev; };
  }, [customer, billTo]);

  const toggle = (job) => setLeft((prev) => {
    const next = new Set(prev);
    if (next.has(job)) next.delete(job); else next.add(job);
    return next;
  });
  const footer = `${E3.name} · Statement for ${billTo}${customer ? ` · Customer # ${customer.code}` : ''} · ${asOf}`;
  const ready = customer && fontsReady && items.length > 0;
  const openCount = items.filter((i) => i.open > 0).length;

  const css = `
.stm-shell{position:fixed;inset:0;z-index:3000;overflow:auto;background:#E6E3DE;font-family:${SANS};color:${INK};-webkit-font-smoothing:antialiased;letter-spacing:normal}
.stm-bar{position:sticky;top:0;z-index:2;display:flex;align-items:center;gap:12px;padding:10px 20px;background:rgba(255,255,255,.94);backdrop-filter:blur(8px);border-bottom:1px solid #E0DDD8;flex-wrap:wrap}
.stm-posted{padding:8px 20px;font-size:13px;color:${GREY};background:rgba(255,255,255,.94);border-bottom:1px solid #E0DDD8}
.stm-posted.behind{color:#8A5A00;background:#FFF6E0;border-bottom-color:#F1D9A0}
.stm-bar .t{flex:1;min-width:160px;font-size:13.5px;color:${GREY};white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.stm-btn{display:inline-flex;align-items:center;gap:7px;height:34px;padding:0 14px;border-radius:8px;font:600 13.5px ${SANS};cursor:pointer;border:1px solid #E0DDD8;background:#fff;color:${INK}}
.stm-btn.primary{background:${GREEN_SPENT};border-color:${GREEN_SPENT};color:#fff}
.stm-btn:disabled{opacity:.55;cursor:default}
.stm-jobs{display:flex;align-items:center;gap:12px;font-size:13px;color:${INK2};flex-wrap:wrap}
.stm-jobs label{display:inline-flex;align-items:center;gap:5px;cursor:pointer;white-space:nowrap}
.stm-pages{display:flex;flex-direction:column;align-items:center;gap:24px;padding:24px 16px 48px}
.stm-pages .stm-sheet{box-shadow:0 1px 2px rgba(0,0,0,.06),0 12px 32px -12px rgba(0,0,0,.18);flex:none}
.stm-edit{outline:none;border-radius:3px;box-shadow:0 0 0 1px transparent;transition:box-shadow .15s}
.stm-edit:hover,.stm-edit:focus{box-shadow:0 0 0 1px #C9C4BA;background:#FBFAF7}
.stm-addr:empty::before{content:'Attn, street, city (type it here)';color:#A8A398}
.stm-blank:empty::before{content:'type it here';color:#A8A398}
@page{size:letter;margin:0}
@media print{
  body.stm-open #root{display:none!important}
  html,body{background:#fff!important;margin:0!important}
  .stm-shell{position:static;overflow:visible;background:none}
  .stm-bar,.stm-posted{display:none!important}
  .stm-pages{display:block;padding:0}
  .stm-pages .stm-sheet{box-shadow:none;break-after:page}
  .stm-pages .stm-sheet:last-child{break-after:auto}
  .stm-edit{box-shadow:none!important;background:none!important}
  .stm-addr:empty,.stm-blank:empty::before{display:none}
  *{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important}
}`;

  return createPortal(
    <div className="stm-shell">
      <style>{css}</style>
      <div className="stm-bar">
        <button type="button" className="stm-btn" onClick={() => navigate('/ar?view=statements')}>← AR / Collections</button>
        <div className="t">{customer ? `Statement · ${billTo} · ${customer.code}` : 'Statement'}</div>
        {customer && customer.jobs.length > 1 && (
          <div className="stm-jobs" role="group" aria-label="Jobs on this statement">
            <span style={{ color: GREY }}>Jobs on it:</span>
            {customer.jobs.map((j) => (
              <label key={j.job} title={j.name}>
                <input type="checkbox" checked={!left.has(j.job)} onChange={() => toggle(j.job)} />
                <span style={{ fontFamily: MONO, fontSize: 12.5 }}>{j.job}</span>
              </label>
            ))}
          </div>
        )}
        <button type="button" className="stm-btn primary" disabled={!ready} onClick={() => window.print()}>Print / Save as PDF</button>
      </div>
      {posted && posted.latest && (
        <div className={`stm-posted${posted.behind ? ' behind' : ''}`} role={posted.behind ? 'status' : undefined}>
          {posted.behind
            ? <><strong>{monthName(posted.lastMonth)} billings haven&rsquo;t been posted yet</strong>, so they aren&rsquo;t on this statement &mdash; the latest posted invoice is {fmtDay(posted.latest)}.</>
            : <>{monthName(posted.lastMonth)} billings are posted &mdash; the latest posted invoice is {fmtDay(posted.latest)}.</>}
        </div>
      )}
      {error && <div style={{ padding: 40, textAlign: 'center', color: RED }}>{error}</div>}
      {!error && !data && <div style={{ padding: 60, textAlign: 'center', color: GREY }}>Building the statement…</div>}
      {data && !customer && <div style={{ padding: 60, textAlign: 'center', color: GREY }}>Nothing is open for {code} in the latest AR pull ({asOf}).</div>}
      {customer && items.length === 0 && <div style={{ padding: 60, textAlign: 'center', color: GREY }}>Tick at least one job to put on the statement.</div>}
      {ready && (
        <div className="stm-pages">
          {pages.map(([from, to], pi) => {
            const last = pi === pages.length - 1;
            const slice = lines.slice(from, to);
            // A later page that starts inside a job says which job it's still on.
            const carry = pi > 0 && multiJob && slice.length && slice[0].kind === 'inv' ? slice[0].i.job : null;
            return (
              <Sheet key={pi} page={pi + 1} pages={pages.length} footer={footer}>
                {pi === 0 ? (
                  <>
                    <div style={{ height: 84, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 24 }}>
                      <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                        <img src={E3.logo} alt="E3 Entegral Solutions" width={72} height={72} style={{ display: 'block', width: 72, height: 72 }} />
                        <div style={{ fontSize: 12, lineHeight: '17px', color: INK2 }}>
                          <div style={{ fontSize: 17, lineHeight: '24px', fontWeight: 600, color: GREEN_HEAD }}>{E3.name}</div>
                          <div>{E3.street}</div>
                          <div>{E3.city}</div>
                          <div>Phone {E3.phone} · Fax {E3.fax}</div>
                        </div>
                      </div>
                      <div style={{ fontSize: 24, lineHeight: '30px', fontWeight: 600, letterSpacing: '.14em', color: GREEN_HEAD }}>STATEMENT</div>
                    </div>
                    <div style={{ height: 112, marginTop: 16, display: 'flex', alignItems: 'flex-start', gap: 20, borderBottom: `2px solid ${GREEN_HEAD}`, boxSizing: 'content-box' }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={label}>Bill to</div>
                        {/* Uncontrolled: React never rewrites what's typed here; on blur the name goes to the
                            running heads, the footer and the PDF's file name. */}
                        <div
                          key={customer.code}
                          className="stm-edit"
                          contentEditable
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}
                          suppressContentEditableWarning
                          spellCheck={false}
                          onBlur={(e) => setName(e.currentTarget.innerText.replace(/\s+/g, ' ').trim() || null)}
                          style={{ fontSize: 15.5, lineHeight: '22px', fontWeight: 600, color: INK, marginTop: 4, whiteSpace: 'nowrap', overflow: 'hidden' }}
                        >
                          {customer.name}
                        </div>
                        <div key={`addr-${customer.code}`} className="stm-edit stm-addr" contentEditable suppressContentEditableWarning spellCheck={false}
                          style={{ fontSize: 12.5, lineHeight: '17px', color: INK2, marginTop: 2, maxHeight: 68, overflow: 'hidden', whiteSpace: 'pre-line' }}>
                          {[ATTN, ...(record ? record.address : [])].join('\n')}
                        </div>
                      </div>
                      <div style={{ width: 232, flex: 'none', paddingTop: 2 }}>
                        <Field name="Job #"><span style={{ fontFamily: MONO }}>{jobText}</span></Field>
                        <Field name="Customer #"><span style={{ fontFamily: MONO }}>{customer.code}</span></Field>
                        <Field name="Customer phone"><span key={`ph-${customer.code}`} className="stm-edit stm-blank" contentEditable suppressContentEditableWarning spellCheck={false} style={{ display: 'inline-block', minWidth: 90 }}>{record && record.phone}</span></Field>
                        <Field name="Customer fax"><span key={`fx-${customer.code}`} className="stm-edit stm-blank" contentEditable suppressContentEditableWarning spellCheck={false} style={{ display: 'inline-block', minWidth: 90 }}>{record && record.fax}</span></Field>
                        <Field name="Date">{asOf}</Field>
                      </div>
                      <div style={{ width: 168, flex: 'none', background: FILL, padding: '10px 14px', textAlign: 'right', boxSizing: 'border-box' }}>
                        <div style={label}>Amount due</div>
                        <div style={{ fontFamily: MONO, fontSize: fitMono(money(aging.total), 136, 20), lineHeight: '30px', fontWeight: 800, color: INK, marginTop: 2, whiteSpace: 'nowrap' }}>{money(aging.total)}</div>
                        <div style={{ fontSize: 11.5, color: GREY }}>{openCount} open invoice{openCount === 1 ? '' : 's'}</div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div style={{ height: RUN_HEAD - 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, borderBottom: `2px solid ${GREEN_HEAD}`, boxSizing: 'content-box', fontSize: 12, color: GREY }}>
                    <div style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <span style={{ fontWeight: 600, letterSpacing: '.1em', color: GREEN_HEAD }}>STATEMENT</span> · <span style={{ color: INK }}>{billTo}</span> · Customer # {customer.code}
                    </div>
                    <div style={{ whiteSpace: 'nowrap' }}>{asOf} (continued)</div>
                  </div>
                )}
                {to > from && <TableHead />}
                {carry && <JobLine job={carry} continued />}
                {slice.map((l) => (l.kind === 'group'
                  ? <JobLine key={`g-${l.job}`} job={l.job} />
                  : <Row key={`${l.i.job}-${l.i.number}`} i={l.i} />))}
                {last && (
                  <div style={{ height: CLOSE_H - 18, marginTop: 18 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${AGING.length + 1}, minmax(0, 1fr))`, gap: 4, height: 56 }}>
                      {AGING.map((b) => (
                        // 11px: "$1,338,738.69" (Ferris, live) is 86px of a tile's 88; anything longer steps down to fit.
                        <div key={b.key} style={{ background: FILL, padding: '8px 4px', textAlign: 'center' }}>
                          <div style={{ ...label, fontSize: 10 }}>{b.label}</div>
                          <div style={{ fontFamily: MONO, fontSize: fitMono(money(aging[b.key]), 88, 11), fontWeight: 600, marginTop: 3, whiteSpace: 'nowrap', color: b.key !== 'current' && aging[b.key] > 0.004 ? RED : INK }}>{money(aging[b.key])}</div>
                        </div>
                      ))}
                      <div style={{ background: GREEN_HEAD, color: '#fff', padding: '8px 4px', textAlign: 'center' }}>
                        <div style={{ ...label, fontSize: 10, color: '#DCE6D5' }}>Amount due</div>
                        <div style={{ fontFamily: MONO, fontSize: fitMono(money(aging.total), 88, 11), fontWeight: 800, marginTop: 3, whiteSpace: 'nowrap' }}>{money(aging.total)}</div>
                      </div>
                    </div>
                    <div style={{ marginTop: 20, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, fontSize: 12, lineHeight: '17px', color: INK2, height: 92 }}>
                      <div>
                        <div style={label}>Remit to</div>
                        <div style={{ marginTop: 4, color: INK, fontWeight: 600 }}>{E3.name}</div>
                        <div>{E3.street}</div>
                        <div>{E3.city}</div>
                        <div>Please include the invoice numbers with your payment.</div>
                      </div>
                      <div>
                        <div style={label}>Questions about this statement</div>
                        <div style={{ marginTop: 4, color: INK, fontWeight: 600 }}>{E3.phone}</div>
                        <div>{E3.email}</div>
                        <div style={{ marginTop: 4 }}>Balances as of {asOf}. Payments made since then may not show yet.</div>
                      </div>
                    </div>
                    <div style={{ marginTop: 14, fontSize: 12, lineHeight: '16px', color: INK, fontWeight: 600 }}>
                      We offer the security and convenience of payment by ACH or wire transfer. Please contact {E3.email} for details.
                    </div>
                    <div style={{ marginTop: 8, fontSize: 12.5, lineHeight: '18px', fontWeight: 600, color: GREEN_HEAD }}>Thank you for the chance to earn your business!</div>
                  </div>
                )}
              </Sheet>
            );
          })}
        </div>
      )}
    </div>,
    document.body,
  );
};

export default StatementPage;
